/**
 * The fan's sign-in state as component state.
 *
 * Two states, not three, and that choice survives the move off cookies. useLoyaltySession is
 * tri-state because "still loading" and "answered" produce different correct renders; here they do
 * not. An `undefined`-while-unknown version would have to render NO CTA for as long as the SDK
 * takes to settle -- LoyaltyJoinHero renders nothing when given neither an href nor an onClick --
 * on a page whose entire purpose is that one button. Starting at the synchronous read and moving
 * when the answer lands means the worst case is a signed-in fan briefly offered "Sign In", which
 * costs them one Auth0 round trip that returns them here correctly labelled.
 *
 * **It CAN now go back to false, and that is a change.** This hook used to read the
 * `auth0_authenticated` cookie through `authSignal.js` -- deleted 2026-09-07 -- which reported a
 * sign-in and, by its own docblock, never a sign-out, so a fan who signed out in another tab kept
 * whatever was on screen until the next navigation. `window.urcAuth.subscribe` reports both, so
 * the label now corrects itself. That is strictly better for a CTA: offering "Join now" to somebody
 * who has just signed out is an invitation to a click that cannot work.
 *
 * `null` from `state()` -- "the SDK has not answered" -- is read as false here on purpose. It is
 * the renderable answer, and the subscription is what corrects it.
 */

import { useEffect, useState } from 'preact/hooks';
import { isSignedIn, subscribeAuth } from './urcAuth';

/**
 * @return {boolean} Whether a fan is established for this browser.
 */
export function useSignedIn() {
	// Seeded from the SYNCHRONOUS read rather than from false: on an in-site navigation the SDK's
	// cache is warm and `state()` may already be populated by the time this renders, so the common
	// case paints the right label on the first frame and never relabels.
	const [ signedIn, setSignedIn ] = useState( isSignedIn );

	useEffect(
		() => subscribeAuth( state => setSignedIn( Boolean( state && state.signedIn ) ) ),
		[]
	);

	return signedIn;
}
